import {
	IconAutomaticGearbox,
	IconBell,
	IconBellOff,
	IconChevronDown,
	IconHelp,
	IconLayoutGrid,
	IconLogout,
	IconTerminal2,
	IconUser,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
	Avatar,
	Button,
	Dropdown,
	Flex,
	type MenuProps,
	Tag,
	Typography,
	theme,
} from "antd";
import { useArtemisAuth } from "@/auth/artemis-auth-provider";
import {
	useIsOrgAdmin,
	useOrganization,
	useRouteOrgSlug,
} from "@/common/artemis-operations/organization";
import { useRealtime } from "@/contexts/realtime-context";
import { useWizards } from "@/contexts/wizard-context";
import { CustomIcon } from "../../base";

/**
 * Org/account identity control: a square avatar + org name + chevron that opens the account menu
 * (account, workspaces, CLI setup, org settings, logout). Sits at the top of the left sidebar
 * (`block`) or standalone in the workspace-picker header.
 */
export function SidebarIdentity({
	block = false,
	minimal = false,
	collapsed = false,
}: {
	block?: boolean;
	/** Hide the workspace-context items (Workspaces, CLI Setup, Restart tour) — e.g. on the picker. */
	minimal?: boolean;
	/** Collapsed sidebar: show only the avatar (no name/tag/chevron). */
	collapsed?: boolean;
}) {
	const { user, logout } = useArtemisAuth();
	const { popupsEnabled, setPopupsEnabled } = useRealtime();
	const { openWelcomeTour, openCliSetupWizard } = useWizards();
	const navigate = useNavigate();
	// The tour points at the workspace sidebar, so only offer it on a workspace page.
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId?: string;
	};
	const { data: org } = useOrganization();
	const { isAdmin } = useIsOrgAdmin();
	const orgSlug = useRouteOrgSlug();
	const {
		token: { colorPrimary },
	} = theme.useToken();

	const orgName =
		(Array.isArray(org) ? org[0]?.name : undefined) ?? "StratoNext";
	const initials = orgName.slice(0, 2).toUpperCase();
	const env = import.meta.env.VITE_ENV as string | undefined;
	const isProdEnv = env === "prod";

	const items: NonNullable<MenuProps["items"]> = [
		{
			key: "identity",
			icon: <CustomIcon icon={IconUser} />,
			label: user?.email,
			onClick: () => navigate({ to: "/account" }),
		},
		...(minimal
			? []
			: [
					{ type: "divider" as const },
					{
						key: "workspaces",
						icon: <CustomIcon icon={IconLayoutGrid} />,
						label: "Workspaces",
						onClick: () => navigate({ to: "/home" }),
					},
					{ type: "divider" as const },
					{
						key: "cli-setup",
						icon: <CustomIcon icon={IconTerminal2} />,
						label: "CLI Setup",
						onClick: () => openCliSetupWizard(),
					},
					...(workspaceId
						? [
								{ type: "divider" as const },
								{
									key: "welcome-tour",
									icon: <CustomIcon icon={IconHelp} />,
									label: "Restart tour",
									onClick: openWelcomeTour,
								},
							]
						: []),
				]),
		...(isAdmin
			? [
					{ type: "divider" as const },
					{
						key: "org-admin",
						icon: <CustomIcon icon={IconAutomaticGearbox} />,
						label: "Organization settings",
						onClick: () =>
							orgSlug &&
							navigate({ to: "/$orgSlug/admin", params: { orgSlug } }),
					},
					{ type: "divider" as const },
				]
			: []),
		{
			key: "notifications",
			icon: <CustomIcon icon={popupsEnabled ? IconBell : IconBellOff} />,
			label: popupsEnabled ? "Notifications: on" : "Notifications: off",
			onClick: () => setPopupsEnabled(!popupsEnabled),
		},
		{ type: "divider" as const },
		{
			key: "logout",
			icon: <CustomIcon icon={IconLogout} />,
			label: "Logout",
			danger: true,
			onClick: () => logout(),
		},
	];

	return (
		<Dropdown menu={{ items }} trigger={["click"]} placement="bottomLeft">
			<Button
				type="text"
				style={{
					height: "auto",
					padding: "6px 8px",
					width: block && !collapsed ? "100%" : undefined,
				}}
			>
				<Flex
					align="center"
					gap={8}
					style={{ width: block && !collapsed ? "100%" : undefined }}
				>
					<Avatar
						shape="square"
						size={24}
						style={{
							backgroundColor: colorPrimary,
							fontSize: 11,
							flex: "0 0 auto",
						}}
					>
						{initials}
					</Avatar>
					{/* Collapsed sidebar: avatar only. */}
					{!collapsed && (
						<>
							<Typography.Text
								strong
								ellipsis
								style={block ? { flex: 1, textAlign: "left" } : undefined}
							>
								{orgName}
							</Typography.Text>
							{env && !isProdEnv && (
								<Tag color="blue" style={{ marginInlineEnd: 0 }}>
									{env.toUpperCase()}
								</Tag>
							)}
							<CustomIcon icon={IconChevronDown} />
						</>
					)}
				</Flex>
			</Button>
		</Dropdown>
	);
}
